import { useCallback, useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useSearchParams } from 'react-router-dom';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function useProviderFilter(): number[] {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    const raw = searchParams.get('providerIds') ?? '';
    return raw
      .split(',')
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0);
  }, [searchParams]);
}

export function ProviderFilter(): React.JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const providerIds = useProviderFilter();
  const { data: providers } = trpcAdmin.workPlacements.placements.listProviderOptions.useQuery(
    undefined,
    {
      initialData: [],
    },
  );

  const options = useMemo(
    () =>
      providers.map((provider) => ({
        id: String(provider.id),
        value: String(provider.id),
        label: provider.name,
      })),
    [providers],
  );

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      const nextProviderIds = filters.providerIds ?? [];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextProviderIds.length === 0) {
            next.delete('providerIds');
          } else {
            next.set('providerIds', nextProviderIds.join(','));
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <MultipleChoiceFilter
      items={options}
      label={t`Provider`}
      name="providerIds"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      value={providerIds.join(',')}
      variant="ghost"
      leftIcon={<Building2 size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
